import { ChevronDown, ChevronLeft, ChevronRight, Import, SearchLoop } from 'assets/icons';
import StyledInput from 'components/styledInput/StyledInput';
import Button from 'components/button';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import { useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Navigate, Outlet, useLocation, useSearchParams } from 'react-router-dom';
import removeAccents from 'remove-accents';
import Fuse from 'fuse.js';
import ApiDatabase from 'server';
import Usefetchdata from 'hooks/usefetchdata';
import PageForm from '../../components/pageForm/PageForm';
import PayslipManagementSubNav from '../../components/subnav/payslipManagement.subNav';

const getInitialDate = (searchParams) => {
  const y = searchParams.get('y');
  const m = searchParams.get('m');
  if (y && m) {
    const d = new Date(Number(y), Number(m) - 1, 1);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
};

const PayslipManagement = () => {
  const { userData } = Usefetchdata();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toggle } = useModal();
  const { setModalData, refreshPayslips, modalDataReturn } = UseContext();

  const [selectedDate, setSelectedDate] = useState(() => getInitialDate(searchParams));
  const [payslips, setPayslips] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isWaiting, setIsWaiting] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [filteredPayslips, setFilteredPayslips] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [countPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [countTotal, setCountTotal] = useState(0);
  const [payslipsToDisplay, setPayslipsToDisplay] = useState([]);

  const token = localStorage.getItem('xsrfToken');

  const year = selectedDate
    ? String(selectedDate.getFullYear())
    : String(new Date().getFullYear());
  const month = selectedDate
    ? String(selectedDate.getMonth() + 1).padStart(2, '0')
    : String(new Date().getMonth() + 1).padStart(2, '0');

  useEffect(() => {
    const y = searchParams.get('y');
    const m = searchParams.get('m');
    if (y && m) {
      const d = new Date(Number(y), Number(m) - 1, 1);
      if (!isNaN(d.getTime()) && (String(d.getFullYear()) !== year || String(d.getMonth() + 1).padStart(2, '0') !== month)) {
        setSelectedDate(d);
      }
    }
  }, [searchParams]);

  useEffect(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('y', year);
      next.set('m', month);
      return next;
    }, { replace: true });
  }, [year, month]);

  const fetchPayslips = () => {
    setIsWaiting(true);
    ApiDatabase.listPayslips(
      { token, year, month },
      (data) => {
        setPayslips(data.payslips || []);
        setTotalAmount(data.totalAmount || 0);
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') console.log(err);
        setIsWaiting(false);
      }
    );
  };

  useEffect(() => {
    fetchPayslips();
    setCurrentPage(1);
    setSelectedIds([]);
  }, [year, month, refreshPayslips, modalDataReturn]);

  // Filter payslips client-side
  useEffect(() => {
    let result = payslips;

    if (statusFilter) {
      result = result.filter((p) => p.status === statusFilter);
    }

    if (search.length > 0) {
      const searchTerm = removeAccents(search.toLowerCase());
      const options = {
        keys: [
          'id_temp_worker_bestt',
          'lastname',
          'firstname',
          'amount',
        ],
        includeScore: true,
        threshold: 0.3,
        getFn: (obj, path) => {
          const value = Fuse.config.getFn(obj, path);
          if (value == null) return '';
          return removeAccents(value.toString().toLowerCase());
        },
      };
      const fuse = new Fuse(result, options);
      result = fuse.search(searchTerm).map((r) => r.item);
    }

    setFilteredPayslips(result);
    setCountTotal(result.length);
    setTotalPages(Math.ceil(result.length / countPerPage));
  }, [payslips, search, statusFilter, countPerPage]);

  useEffect(() => {
    setPayslipsToDisplay(
      filteredPayslips.slice(
        (currentPage - 1) * countPerPage,
        currentPage * countPerPage
      )
    );
  }, [filteredPayslips, currentPage, countPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const [multiDropdownOpen, setMultiDropdownOpen] = useState(false);
  const multiDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (multiDropdownRef.current && !multiDropdownRef.current.contains(e.target))
        setMultiDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  if (userData?._id && !userData?.accessPayslip) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleMultipleImport = () => {
    setMultiDropdownOpen(false);
    const hasSelection = selectedIds.length > 0;
    const target = hasSelection
      ? payslips.filter((p) => selectedIds.includes(p.id_temp_worker_bestt))
      : payslips;
    if (target.length === 0) return;
    setModalData({ payslips: target, year, month, preSelected: hasSelection });
    toggle('payslip_import_global');
  };

  const handleMultipleDelete = () => {
    setMultiDropdownOpen(false);
    const target = selectedIds.length > 0
      ? payslips.filter(
          (p) => selectedIds.includes(p.id_temp_worker_bestt) && p.status === 'imported'
        )
      : payslips.filter((p) => p.status === 'imported');
    if (target.length === 0) return;
    setModalData({ payslips: target, year, month });
    toggle('payslip_delete');
  };

  const handleExportCSV = () => {
    window.open(
      `${process.env.REACT_APP_API_URL}admin/exportPayslipsCSV?year=${year}&month=${month}&token=${token}`,
      '_blank'
    );
  };

  const isHistory = location.pathname.includes('history') || location.pathname.includes('stats');

  return (
    <PageForm>
      <div className="p-3 flex flex-col w-full h-full gap-3">
        <div className="flex justify-end text-sm">
          <PayslipManagementSubNav />
        </div>
        {!isHistory && <><div className="flex items-center justify-between">
          <div className="flex gap-3 items-center">
            <div className="flex items-center gap-1 text-sm">
              <button
                className="flex items-center justify-center w-9 h-9 rounded-lg border hover:bg-gray-100"
                onClick={() =>
                  setSelectedDate(
                    (prev) =>
                      new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                  )
                }
              >
                <ChevronLeft wh={16} color="#374151" />
              </button>
              <DatePicker
                selected={selectedDate}
                onChange={(date) => setSelectedDate(date)}
                showMonthYearPicker
                dateFormat="yyyy/MM"
                className="w-full min-w-[110px] h-10 ft-b border rounded-lg text-center text-sm max-h-[38px]"
                locale="fr-FR"
              />
              <button
                className="flex items-center justify-center w-9 h-9 rounded-lg border hover:bg-gray-100"
                onClick={() =>
                  setSelectedDate(
                    (prev) =>
                      new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                  )
                }
              >
                <ChevronRight wh={16} color="#374151" />
              </button>
            </div>
            <div className="max-w-[160px]">
              <select
                className="w-full h-10 pl-2 pr-3 ft-b placeholder-gray-600 border rounded-lg text-sm"
                onChange={(e) => setStatusFilter(e.target.value)}
                value={statusFilter}
              >
                <option value="">Tous</option>
                <option value="imported">Importé</option>
                <option value="not_imported">Non importé</option>
              </select>
            </div>
          </div>
          <div className="max-w-[250px] text-sm">
            <StyledInput
              type="text"
              placeholder="Rechercher"
              imgFront={<SearchLoop wh={20} color="#6B7280" />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <div className="border rounded py-2 px-4 text-sm font-medium bg-gray-50">
            Total : {isWaiting ? <span className="inline-block w-16 h-4 bg-gray-200 rounded animate-pulse align-middle" /> : `${totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`}
          </div>
          <div className="relative" ref={multiDropdownRef}>
            <Button
              onClick={() => setMultiDropdownOpen(!multiDropdownOpen)}
              color="PRIMARY"
            >
              <div className="flex gap-1 items-center">
                <span>Actions{selectedIds.length > 0 ? ` (${selectedIds.length})` : ''}</span>
                <ChevronDown wh={20} color="#FFFFFF" />
              </div>
            </Button>
            {multiDropdownOpen && (
              <div className="absolute border-2 border-[#F3F4F6] rounded-lg shadow-lg w-56 top-10 z-50 bg-gray-50">
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                  onClick={handleMultipleImport}
                >
                  <Import wh={16} color="#374151" />
                  Importer
                </div>
                <div className="border-b border-gray-100" />
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-red-500"
                  onClick={handleMultipleDelete}
                >
                  Supprimer
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-2 items-center ml-auto">
            {location.pathname.includes('payslips') && (
              <Button size="SMI" color="SECONDARY" onClick={handleExportCSV}>
                <div className="flex gap-1 items-center text-sm">Export CSV</div>
              </Button>
            )}
          </div>
        </div></>}
        <Outlet
          context={{
            payslipsToDisplay,
            currentPage,
            setCurrentPage,
            totalPages,
            countPerPage,
            countTotal,
            isWaiting,
            selectedIds,
            setSelectedIds,
            filteredPayslips,
            payslips,
            year,
            month,
            fetchPayslips,
          }}
        />
      </div>
    </PageForm>
  );
};

export default PayslipManagement;
